import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Download, X, Share } from "lucide-react";

const DISMISS_KEY = "sj_pwa_install_dismiss";
const DISMISS_DAYS = 14;

function isStandalone() {
  return window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
}
function isMobile() {
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
}
function isIOS() {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}
function dismissedRecently() {
  const t = Number(localStorage.getItem(DISMISS_KEY) || 0);
  return t && Date.now() - t < DISMISS_DAYS * 864e5;
}

export function InstallPrompt() {
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [iosHelp, setIosHelp] = useState(false);

  useEffect(() => {
    if (!user || user.role !== "contractor") return;
    if (isStandalone() || !isMobile() || dismissedRecently()) return;
    // Native prompt captured in index.js; also allow iOS (no beforeinstallprompt).
    const ready = () => setShow(true);
    if (window.__sjDeferredPrompt || isIOS()) setShow(true);
    window.addEventListener("sj-can-install", ready);
    return () => window.removeEventListener("sj-can-install", ready);
  }, [user]);

  if (!show) return null;

  const dismiss = () => { localStorage.setItem(DISMISS_KEY, String(Date.now())); setShow(false); };
  const add = async () => {
    const dp = window.__sjDeferredPrompt;
    if (dp) {
      dp.prompt();
      try { await dp.userChoice; } catch { /* noop */ }
      window.__sjDeferredPrompt = null;
      setShow(false);
    } else if (isIOS()) {
      setIosHelp(true);
    } else {
      setIosHelp(true);
    }
  };

  return (
    <div data-testid="pwa-install-prompt" className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[92%] max-w-md rounded-2xl bg-slate-900 text-white shadow-2xl border border-slate-700 p-4">
      <button data-testid="pwa-install-close" onClick={dismiss} className="absolute top-3 right-3 text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-blue-600 flex items-center justify-center shrink-0"><Download className="w-6 h-6" /></div>
        <div className="min-w-0">
          <h3 className="font-head font-semibold text-sm">Add SJ Field to your home screen</h3>
          <p className="text-xs text-slate-300 mt-0.5">Get faster access to jobs, photos, check-ins, and offline field tools.</p>
          {iosHelp && (
            <p className="text-xs text-blue-300 mt-2 flex items-center gap-1" data-testid="pwa-ios-help">Tap <Share className="w-3.5 h-3.5" /> then "Add to Home Screen".</p>
          )}
          <div className="flex gap-2 mt-3">
            <Button data-testid="pwa-install-add" size="sm" onClick={add} className="bg-blue-600 hover:bg-blue-700 h-8">Add App</Button>
            <Button data-testid="pwa-install-notnow" size="sm" variant="ghost" onClick={dismiss} className="h-8 text-slate-300 hover:text-white hover:bg-slate-800">Not Now</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
